import React, { useCallback, useState } from 'react';
import cx from 'classnames';
import moment from 'moment';

import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as DropdownIcon } from 'assets/icons/arrow-down.svg';
import { ReactComponent as GreenCheck } from 'assets/icons/green-check.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import DentalButton from 'components/Buttons/DentalButton/DentalButton';
import { renderToast } from 'components/CustomToast/CustomToast';
import IconTooltip from 'components/IconTooltip/IconTooltip';
import { BookingPatient } from 'interfaces/familyBookingTypes';
import { IClinicServicesOption } from 'pages/ClinicDetailsPage/BookNowPanel/BookNowPanel';
import {
  MYSELF_AND_OTHERS_OPTION,
  ModeOption,
} from 'pages/ClinicDetailsPage/BookNowPanel/utils/constants';
import {
  updateBookingPatient,
  updateContactUser,
} from 'redux/familyBookingSlice';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { concatFullName } from 'utils/common';
import { formatFullPhoneNumberV2 } from 'utils/formatPhoneNumber';

import ReasonDropdownSection from '../../ReasonDropdownSection/ReasonDropdownSection';
import {
  MAX_PATIENTS_MESSAGE,
  UNBOOKABLE_PATIENT_MESSAGE,
  UNBOOKABLE_POINT_OF_CONTACT_MESSAGE,
  getDisabledServiceIds,
  getSortedServiceOptions,
  isAllowedToAddPatient,
  isPatientBookable,
} from '../../utils/utils';
import PractitionerSelector, {
  NO_PREFERENCE_PRACTITIONER,
  PractitionerValue,
} from '../PractitionerSelector/PractitionerSelector';
import styles from './PatientRow.module.scss';

interface PatientRowProps {
  patient: BookingPatient;
  mode: ModeOption;
  services: IClinicServicesOption[];
  isPointOfContact?: boolean;
}

const PatientRow = ({
  patient,
  mode,
  services,
  isPointOfContact = false,
}: PatientRowProps) => {
  const dispatch = useAppDispatch();

  const {
    familyBookingSlice: { patients },
    clinicDetailsSlice: { clinicData },
  } = useAppSelector((state) => state);

  const isPointOfContactIncluded = mode.id === MYSELF_AND_OTHERS_OPTION.id;

  const canSchedule = !isPointOfContact || isPointOfContactIncluded;

  const isBookable = isPatientBookable(patient);

  const [isExpanded, setIsExpanded] = useState(
    isPointOfContact && canSchedule && isBookable
  );
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(
    !isPointOfContact && !!patient.serviceId
  );

  const serviceOptions = getSortedServiceOptions(services, patient);
  const disabledServiceIds = getDisabledServiceIds(patient);
  const service = serviceOptions.find((item) => item.id === patient.serviceId);
  const isScheduled = !!patient.serviceId;

  const practitioner: PractitionerValue = patient.preferredPractitionerId
    ? {
        id: patient.preferredPractitionerId,
        title:
          patient.preferredPractitionerTitle ||
          NO_PREFERENCE_PRACTITIONER.title,
      }
    : NO_PREFERENCE_PRACTITIONER;

  const dentist: PractitionerValue = patient.preferredDentistId
    ? {
        id: patient.preferredDentistId,
        title:
          patient.preferredDentistTitle || NO_PREFERENCE_PRACTITIONER.title,
      }
    : NO_PREFERENCE_PRACTITIONER;

  const isSelectedServiceDisabled =
    !!service && disabledServiceIds.includes(service.id);

  const isScheduleConfirmed = canSchedule && isScheduled;

  //--Not derived from isExpanded alone: an unbookable row must keep offering the
  //--disabled button rather than the caret, however it was mounted
  const isExpandToggleDisplayed = isScheduled || (isExpanded && isBookable);

  const isUnbookablePointOfContact =
    isPointOfContact && canSchedule && !isBookable;

  const hasPhoneNumber = !!patient.phoneNumber;

  const hasEmail = !!patient.email;

  const hasLastHygieneAppt = !!patient.lastHygieneAppt?.startDate;

  const updatePatient = useCallback(
    (changes: Partial<BookingPatient>) =>
      dispatch(
        isPointOfContact
          ? updateContactUser(changes)
          : updateBookingPatient({ ...patient, ...changes })
      ),
    [dispatch, isPointOfContact, patient]
  );

  const onClickSchedule = () => {
    if (!isPointOfContact && !isAllowedToAddPatient({ patients, mode })) {
      renderToast({ message: MAX_PATIENTS_MESSAGE });

      return;
    }

    setIsExpanded(true);
  };

  const onClickRemove = () => {
    updatePatient({
      serviceId: null,
      isDisabledService: false,
      preferredPractitionerId: null,
      preferredPractitionerTitle: null,
      preferredDentistId: null,
      preferredDentistTitle: null,
    });

    setIsExpanded(false);
  };

  const onChangeService = (option: { id: string }) =>
    updatePatient({
      serviceId: option.id,
      isDisabledService: disabledServiceIds.includes(option.id),
      preferredPractitionerId: null,
      preferredPractitionerTitle: null,
      preferredDentistId: null,
      preferredDentistTitle: null,
    });

  const onChangePractitioner = (option: PractitionerValue) => {
    const isNoPreference = option.id === NO_PREFERENCE_PRACTITIONER.id;

    updatePatient({
      preferredPractitionerId: isNoPreference ? null : option.id,
      preferredPractitionerTitle: isNoPreference ? null : option.title,
    });
  };

  const onChangeDentist = (option: PractitionerValue) => {
    const isNoPreference = option.id === NO_PREFERENCE_PRACTITIONER.id;

    updatePatient({
      preferredDentistId: isNoPreference ? null : option.id,
      preferredDentistTitle: isNoPreference ? null : option.title,
    });
  };

  return (
    <div
      className={cx(styles['container'], {
        [styles['container-point-of-contact']]: isPointOfContact,
      })}
    >
      <div className={styles['patient-summary']}>
        <div
          className={cx(styles['avatar'], {
            [styles['avatar-point-of-contact']]: isPointOfContact,
          })}
        >
          {`${patient.firstName?.[0] || ''}${patient.lastName?.[0] || ''}`}
        </div>

        <div className={styles['patient-info']}>
          <div className={styles['patient-name']}>
            <b>{concatFullName(patient.firstName, patient.lastName)}</b>

            {isPointOfContact && <span>(Point of contact)</span>}

            {isScheduleConfirmed && <GreenCheck />}
          </div>

          <div className={styles['patient-details']}>
            <span>
              {moment(patient.dob, ['MM/DD/YYYY', 'YYYY-MM-DD']).format(
                'MM/DD/YYYY'
              )}
            </span>

            {hasPhoneNumber && (
              <>
                <span>•</span>
                <span>{formatFullPhoneNumberV2(patient.phoneNumber)}</span>
              </>
            )}

            {hasEmail && (
              <>
                <span>•</span>
                <span className={styles['patient-email']}>{patient.email}</span>
              </>
            )}
          </div>

          {hasLastHygieneAppt && (
            <div className={styles['last-hygiene-appt']}>
              {`Last cleaning: ${moment(
                patient.lastHygieneAppt?.startDate,
                'YYYY-MM-DD'
              ).format('MMM DD, YYYY')}`}
            </div>
          )}

          {isScheduleConfirmed && (
            <div className={styles['service-summary']}>
              <span>{service?.title}</span>
              <span>•</span>
              <span>{practitioner.title}</span>
            </div>
          )}
        </div>

        {canSchedule &&
          (isExpandToggleDisplayed ? (
            <button
              type="button"
              className={styles['expand-button']}
              onClick={() => setIsExpanded((value) => !value)}
            >
              <DropdownIcon
                className={cx(styles['row-caret'], {
                  [styles['row-caret-expanded']]: isExpanded,
                })}
              />
            </button>
          ) : (
            <div className={styles['schedule-action']}>
              {!isBookable && (
                <IconTooltip
                  place="left"
                  IconComponent={InfoIcon}
                  id="unbookable-patient"
                  content={UNBOOKABLE_PATIENT_MESSAGE}
                />
              )}

              <DentalButton
                variant="outlined"
                type="button"
                className={styles['schedule-btn']}
                onClick={onClickSchedule}
                disabled={!isBookable}
              >
                Schedule
              </DentalButton>
            </div>
          ))}
      </div>

      {isUnbookablePointOfContact && (
        <div className={styles['unbookable-note']}>
          {UNBOOKABLE_POINT_OF_CONTACT_MESSAGE}
        </div>
      )}

      {canSchedule && isExpanded && isBookable && (
        <div className={styles['expanded-reason']}>
          <div className={styles['selection-grid']}>
            <ReasonDropdownSection
              isSingular
              isShownTitle
              isPopperFullWidth
              isOpen={isServiceDropdownOpen}
              options={serviceOptions}
              optionValue={service ?? null}
              onChangeIsOpen={setIsServiceDropdownOpen}
              onChangeServiceOption={onChangeService}
              placeholder={isPointOfContact ? 'Reason' : 'Select reason'}
              isReasonDropdown={isPointOfContact}
              groupBy={(option: any) => option.isBookable}
              getOptionDisabled={(option: any) =>
                disabledServiceIds.includes(option.id)
              }
              isSelectedServiceDisabled={isSelectedServiceDisabled}
              clinicData={clinicData}
            />

            {clinicData.isPreferredPractitionerEnabled && (
              <PractitionerSelector
                clinicId={clinicData.id}
                serviceId={patient.serviceId}
                isExamAndCleaningService={service?.isExamAndCleaningService}
                value={practitioner}
                dentistValue={dentist}
                onChange={onChangePractitioner}
                onChangeDentist={onChangeDentist}
              />
            )}
          </div>

          {!isPointOfContact && isScheduled && (
            <button
              type="button"
              className={styles['remove']}
              onClick={onClickRemove}
            >
              <DeleteIcon />
              <span>Remove from this booking</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PatientRow;
